import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Camera, FileText, RefreshCw, Type, Upload, X } from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Button } from "@/platform/ui/button";
import { Badge } from "@/platform/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/platform/ui/card";
import { GradingProgress } from "@/platform/ui/grading-progress";
import { showCorrectionError } from "@/platform/billing/errors";
import { useSession } from "@/platform/auth/use-session";
import {
  gradeDiscursive,
  listDiscursiveQuestions,
  uploadDiscursiveImages,
  type DiscursiveDiscipline,
  type DiscursiveQuestionRow,
  type GradeDiscursiveResult,
} from "@/tenants/einstein/features/discursive/api";
import { cn } from "@/platform/lib/cn";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";

type Mode = "text" | "image";
const MAX_PAGES = 4;

const DISCIPLINES: { value: DiscursiveDiscipline; label: string }[] = [
  { value: "biologia", label: "Biologia" },
  { value: "quimica", label: "Química" },
  { value: "fisica", label: "Física" },
  { value: "matematica", label: "Matemática" },
  { value: "ingles", label: "Inglês" },
];

function generateUuid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function DiscursivePage() {
  const { session } = useSession();
  const navigate = useNavigate();
  const [discipline, setDiscipline] = useState<DiscursiveDiscipline>("biologia");
  const [questions, setQuestions] = useState<DiscursiveQuestionRow[]>([]);
  const [selectedQ, setSelectedQ] = useState<DiscursiveQuestionRow | null>(null);
  const [mode, setMode] = useState<Mode>("text");
  const [answer, setAnswer] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<GradeDiscursiveResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = await listDiscursiveQuestions(discipline);
        if (cancelled) return;
        setQuestions(qs);
        setSelectedQ(qs[0] ?? null);
        setAnswer("");
        setFiles([]);
        setResult(null);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Falha ao carregar questões.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [discipline]);

  function pickRandomQ() {
    if (questions.length === 0) return;
    // Evita repetir a questão atual quando há pelo menos 2 disponíveis.
    let pool = questions;
    if (selectedQ && questions.length > 1) {
      pool = questions.filter((q) => q.id !== selectedQ.id);
    }
    const i = Math.floor(Math.random() * pool.length);
    setSelectedQ(pool[i] ?? null);
    setAnswer("");
    setFiles([]);
    setResult(null);
  }

  function onFilesPicked(picked: FileList | null) {
    if (!picked) return;
    const arr = Array.from(picked).slice(0, MAX_PAGES - files.length);
    const valid = arr.filter((f) => /^image\/(jpeg|png|webp)$/.test(f.type) && f.size < 20 * 1024 * 1024);
    if (valid.length < arr.length) {
      toast.error("Aceito apenas JPG/PNG/WebP até 20 MB.");
    }
    setFiles((prev) => [...prev, ...valid].slice(0, MAX_PAGES));
  }

  async function handleSubmit() {
    if (!session || !selectedQ) return;
    setSubmitting(true);
    setResult(null);
    try {
      let imagePaths: string[] | undefined;
      if (mode === "image") {
        if (files.length === 0) {
          toast.error("Anexe pelo menos uma foto.");
          setSubmitting(false);
          return;
        }
        const submissionUuid = generateUuid();
        imagePaths = await uploadDiscursiveImages({
          userId: session.user.id,
          submissionUuid,
          files,
        });
      } else if (answer.trim().length < 5) {
        toast.error("Resposta muito curta.");
        setSubmitting(false);
        return;
      }

      const out = await gradeDiscursive({
        discipline,
        question_id: selectedQ.id,
        stem: selectedQ.stem,
        ...(mode === "text" ? { student_answer: answer } : { image_paths: imagePaths }),
      });
      setResult(out);
      toast.success("Correção pronta.");
    } catch (err) {
      showCorrectionError(toast, err, navigate, "Falha na correção.");
    } finally {
      setSubmitting(false);
    }
  }

  if (result) {
    return (
      <DiscursiveResultView
        result={result}
        onAgain={() => {
          setResult(null);
          pickRandomQ();
        }}
        onPainel={() => navigate("/painel")}
      />
    );
  }

  return (
    <div className="relative overflow-hidden bg-mesh">
      <GradingProgress active={submitting} />
      <Blob
        color="mint"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Blob
        color="violet"
        className="pointer-events-none absolute -right-32 top-80 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="pink"
        className="pointer-events-none absolute right-12 top-24 w-12 opacity-60 animate-float"
      />

      <Container size="wide" className="relative py-14 md:py-20">
        <header className="animate-fade-up max-w-3xl">
          <Badge variant="accentSolid">
            <FileText className="size-3" aria-hidden />
            Discursivas
          </Badge>
          <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
            Treine uma discursiva <span className="text-gradient-accent">por dia.</span>
          </h1>
          <p className="mt-2 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
            Bio, Quím, Fís, Mat ou Inglês. Cada questão vale 6 pontos.
          </p>
        </header>

      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_320px]">
        <Card className="p-7 md:p-8">
          <CardHeader className="p-0">
            <CardTitle>1. Disciplina</CardTitle>
          </CardHeader>
          <CardContent className="mt-4 p-0">
            <div className="flex flex-wrap gap-2">
              {DISCIPLINES.map((d) => (
                <button
                  key={d.value}
                  type="button"
                  onClick={() => setDiscipline(d.value)}
                  className={cn(
                    "rounded-full border-2 px-4 py-1.5 text-[length:var(--text-body-sm)] font-medium transition-colors duration-150 ease-out active:scale-[0.97]",
                    discipline === d.value
                      ? "border-[--color-accent] bg-[color:var(--color-accent)] text-white shadow-[var(--shadow-accent)]"
                      : "border-[--color-border] bg-[color:var(--color-card)] text-[color:var(--color-muted-foreground)] hover:border-[--color-accent]/40 hover:text-[color:var(--color-foreground)]",
                  )}
                >
                  {d.label}
                </button>
              ))}
            </div>

            <div className="mt-8 flex items-end justify-between gap-2">
              <div>
                <p className="font-display text-[length:var(--text-h4)] font-semibold leading-[var(--text-h4--line-height)] tracking-[var(--text-h4--letter-spacing)]">
                  2. Questão
                </p>
                <p className="mt-1 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                  {questions.length} disponíveis em {DISCIPLINES.find((d) => d.value === discipline)?.label}
                </p>
              </div>
              <Button variant="secondary" size="sm" onClick={pickRandomQ} disabled={questions.length === 0}>
                <RefreshCw className="size-3.5" aria-hidden />
                Sortear outra
              </Button>
            </div>

            {selectedQ ? (
              <div className="mt-4 rounded-lg border border-[--color-border] bg-[color:var(--color-paper)] p-5">
                <div className="flex items-center gap-2">
                  <Badge variant="accent">Q{selectedQ.number ?? "?"} · {selectedQ.exam_year}</Badge>
                  {selectedQ.topic ? <Badge variant="mint">{selectedQ.topic}</Badge> : null}
                </div>
                <p className="mt-4 whitespace-pre-wrap text-[length:var(--text-body-sm)] leading-relaxed">{selectedQ.stem}</p>
              </div>
            ) : (
              <p className="mt-4 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                Sem questões discursivas dessa disciplina no banco ainda.
              </p>
            )}

            <div className="mt-8">
              <p className="font-display text-[length:var(--text-h4)] font-semibold leading-[var(--text-h4--line-height)] tracking-[var(--text-h4--letter-spacing)]">
                3. Sua resposta
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <ModeButton
                  active={mode === "text"}
                  icon={<Type className="size-4" aria-hidden />}
                  label="Digitar"
                  onClick={() => setMode("text")}
                />
                <ModeButton
                  active={mode === "image"}
                  icon={<Camera className="size-4" aria-hidden />}
                  label="Foto manuscrita"
                  onClick={() => setMode("image")}
                />
              </div>

              {mode === "text" ? (
                <textarea
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  rows={10}
                  placeholder="Resposta técnica. Mostre o caminho — justificativa pontua tanto quanto resposta certa."
                  className="mt-4 w-full rounded-lg border border-[--color-border] bg-[color:var(--color-card)] p-4 text-[length:var(--text-body)] leading-relaxed focus:border-[--color-accent] focus:outline-none focus:ring-4 focus:ring-[--color-ring]"
                />
              ) : (
                <ImageUploader
                  files={files}
                  onPick={onFilesPicked}
                  onRemove={(i) => setFiles((prev) => prev.filter((_, k) => k !== i))}
                />
              )}
            </div>

            <div className="mt-8 sm:flex sm:justify-end">
              <Button
                size="xl"
                variant="gradient"
                onClick={handleSubmit}
                disabled={submitting || !selectedQ}
                loading={submitting}
                className="w-full sm:w-auto"
              >
                {submitting ? "Corrigindo…" : "Enviar para correção"}
                <ArrowUpRight className="size-4" aria-hidden />
              </Button>
            </div>
          </CardContent>
        </Card>

        <aside className="space-y-4">
          <Card className="p-6">
            <Badge variant="accent">Como avaliamos</Badge>
            <ul className="mt-4 space-y-2 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
              <li className="flex gap-2"><span aria-hidden className="mt-1.5 inline-block size-1.5 rounded-full bg-[color:var(--color-accent)] shrink-0" /> Conceito + justificativa + cálculo</li>
              <li className="flex gap-2"><span aria-hidden className="mt-1.5 inline-block size-1.5 rounded-full bg-[color:var(--color-pink)] shrink-0" /> Cada questão vale 6 pontos</li>
              <li className="flex gap-2"><span aria-hidden className="mt-1.5 inline-block size-1.5 rounded-full bg-[color:var(--color-mint)] shrink-0" /> Resposta mínima e excelente</li>
              <li className="flex gap-2"><span aria-hidden className="mt-1.5 inline-block size-1.5 rounded-full bg-[color:var(--color-yellow-deep)] shrink-0" /> Tempo 30–60s</li>
            </ul>
          </Card>
          <Card className="p-6 bg-[color:var(--color-paper)]">
            <Badge variant="pink">Dica Vunesp</Badge>
            <p className="mt-3 text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)] text-pretty">
              Banca recompensa quem mostra o método. Resposta seca sem justificativa perde 50% da
              nota mesmo estando certa.
            </p>
          </Card>
        </aside>
      </div>
      </Container>
    </div>
  );
}

function ModeButton({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center justify-center gap-2 rounded-full border-2 p-3 text-[length:var(--text-body-sm)] font-medium transition-colors duration-150 ease-out active:scale-[0.97]",
        active
          ? "border-[--color-accent] bg-[color:var(--color-accent)] text-white shadow-[var(--shadow-accent)]"
          : "border-[--color-border] bg-[color:var(--color-card)] text-[color:var(--color-muted-foreground)] hover:border-[--color-accent]/40 hover:text-[color:var(--color-foreground)]",
      )}
    >
      {icon}
      {label}
    </button>
  );
}

function ImageUploader({
  files,
  onPick,
  onRemove,
}: {
  files: File[];
  onPick: (l: FileList | null) => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div className="mt-4 space-y-3">
      <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-[--color-border] bg-[color:var(--color-muted)]/40 p-8 text-center transition hover:bg-[color:var(--color-muted)]/60">
        <Upload className="size-5 text-[color:var(--color-muted-foreground)]" aria-hidden />
        <span className="text-[length:var(--text-body-sm)]">
          Anexar foto (máx {MAX_PAGES})
        </span>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => onPick(e.target.files)}
        />
      </label>
      {files.length > 0 ? (
        <ul className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {files.map((f, i) => (
            <li
              key={`${f.name}-${i}`}
              className="relative overflow-hidden rounded-sm border border-[--color-border]"
            >
              <img src={URL.createObjectURL(f)} alt={`Página ${i + 1}`} className="aspect-[3/4] w-full object-cover" />
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="absolute right-1 top-1 grid size-7 place-items-center rounded-xs bg-[color:var(--color-background)]/90"
                aria-label={`Remover página ${i + 1}`}
              >
                <X className="size-3.5" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function DiscursiveResultView({
  result,
  onAgain,
  onPainel,
}: {
  result: GradeDiscursiveResult;
  onAgain: () => void;
  onPainel: () => void;
}) {
  const total = result.total;
  const pct = Math.round((total / 6) * 100);
  return (
    <div className="relative overflow-hidden bg-mesh">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Blob
        color="mint"
        className="pointer-events-none absolute -right-32 top-60 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-10 top-32 w-12 opacity-60 animate-float"
      />

      <Container size="wide" className="relative py-14 md:py-20">
        <header className="animate-fade-up max-w-3xl">
          <Badge variant="accentSolid">Correção concluída</Badge>
          <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
            <span data-tabular="true" className="text-gradient-accent">
              {total.toFixed(1)}
            </span>{" "}
            de 6 pontos.
          </h1>
          <p className="mt-2 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
            {result.source === "image" ? "Resposta manuscrita transcrita pela IA" : "Texto digitado"} · confiança{" "}
            <span data-tabular="true" className="font-semibold">
              {(result.confidence * 100).toFixed(0)}%
            </span>
          </p>
        </header>

        <Card className="mt-8 p-6">
          <div className="flex items-center justify-between">
            <Badge variant="accent">Pontuação total</Badge>
            <Badge variant={pct >= 70 ? "mint" : pct >= 40 ? "accent" : "pink"}>
              {pct}%
            </Badge>
          </div>
          <p
            data-tabular="true"
            className="mt-4 font-display text-[length:var(--text-h1)] font-bold leading-none tracking-[var(--text-h1--letter-spacing)]"
          >
            {total.toFixed(1)}
            <span className="text-[length:var(--text-body-sm)] font-normal text-[color:var(--color-muted-foreground)]"> / 6</span>
          </p>
          <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-[color:var(--color-muted)]">
            <div
              className={`h-full rounded-full transition-all duration-700 ${pct >= 70 ? "bg-[color:var(--color-mint)]" : pct >= 40 ? "bg-[color:var(--color-accent)]" : "bg-[color:var(--color-pink)]"}`}
              style={{ width: `${pct}%` }}
              aria-hidden
            />
          </div>
        </Card>

        <div className="mt-5 grid gap-3 md:grid-cols-3">
          {Object.entries(result.scores).map(([k, v]) =>
            k === "total" ? null : (
              <Card key={k} className="p-5">
                <p className="text-[length:var(--text-overline)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
                  {k}
                </p>
                <p
                  data-tabular="true"
                  className="mt-2 font-display text-[length:var(--text-h2)] font-bold leading-none"
                >
                  {Number(v).toFixed(1)}
                </p>
              </Card>
            ),
          )}
        </div>

        <Card className="mt-6 p-7">
          <CardHeader className="p-0">
            <Badge variant="accent">Feedback</Badge>
          </CardHeader>
          <CardContent className="mt-4 p-0">
            <p className="whitespace-pre-wrap text-[length:var(--text-body)] leading-relaxed text-pretty">
              {result.feedback}
            </p>
          </CardContent>
        </Card>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Card className="p-7">
            <CardHeader className="p-0">
              <Badge variant="mint">Resposta mínima</Badge>
              <CardDescription className="mt-2">O que basta para pontuar.</CardDescription>
            </CardHeader>
            <CardContent className="mt-3 p-0">
              <p className="whitespace-pre-wrap text-[length:var(--text-body-sm)] leading-relaxed text-pretty">
                {result.minimal_answer}
              </p>
            </CardContent>
          </Card>
          <Card className="p-7 bg-[color:var(--color-paper)]">
            <CardHeader className="p-0">
              <Badge variant="yellow">Resposta excelente</Badge>
              <CardDescription className="mt-2">Onde mira a nota máxima.</CardDescription>
            </CardHeader>
            <CardContent className="mt-3 p-0">
              <p className="whitespace-pre-wrap text-[length:var(--text-body-sm)] leading-relaxed text-pretty">
                {result.excellent_answer}
              </p>
            </CardContent>
          </Card>
        </div>

        {result.transcription ? (
          <details className="mt-6">
            <summary className="cursor-pointer text-[length:var(--text-body-sm)] font-semibold text-[color:var(--color-muted-foreground)] hover:text-[color:var(--color-accent)]">
              Ver transcrição da resposta manuscrita
            </summary>
            <Card className="mt-3 p-6">
              <p className="whitespace-pre-wrap text-[length:var(--text-body)] leading-relaxed text-pretty">
                {result.transcription}
              </p>
            </Card>
          </details>
        ) : null}

        <div className="mt-10 flex flex-wrap gap-3">
          <Button size="xl" variant="gradient" onClick={onAgain}>
            Treinar outra
            <ArrowUpRight className="size-4" aria-hidden />
          </Button>
          <Button size="xl" variant="outline" onClick={onPainel}>
            Voltar ao painel
          </Button>
        </div>
      </Container>
    </div>
  );
}
